import * as React from "react";
import { cn } from "@/lib/utils";
import { GradientText, SectionLabel } from "./GradientText";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface FAQItem {
  name: string;
  acceptedAnswer: {
    text: string;
  };
}

interface FAQProps {
  title: string;
  items: FAQItem[];
}

export function FAQ({ title, items }: FAQProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="faq" className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={cn(
            "text-center max-w-3xl mx-auto mb-16 lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel className="mb-6">FAQ</SectionLabel>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
            <GradientText>{title}</GradientText>
          </h2>
        </div>

        <div
          className={cn(
            "max-w-3xl mx-auto",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
          style={{ transitionDelay: isVisible ? "150ms" : "0ms" }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {items.map((item, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className={cn(
                  "rounded-2xl overflow-hidden",
                  "bg-card",
                  "border border-border",
                  "shadow-sm",
                  "data-[state=open]:shadow-md",
                  "transition-shadow duration-300"
                )}
              >
                <AccordionTrigger
                  className={cn(
                    "px-6 py-5 text-left hover:no-underline",
                    "text-foreground font-medium",
                    "hover:text-foreground",
                    "[&[data-state=open]>svg]:rotate-180",
                    "[&>svg]:text-accent [&>svg]:transition-transform [&>svg]:duration-300"
                  )}
                >
                  {item.name}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-5">
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {item.acceptedAnswer.text}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
